import { GetGeotabVehiclesFn, GeotabVehiclesResult } from "../../../../../rest/Geotab";
import { SearchFn, requestServiceAccounts, EntitySearchResult } from "../../../../../rest/Search";
import { GetVehicleTagsFn } from "../../../../../rest/Misc";

import { IdName, Source, Division, ServiceAccount, Vehicle, EntityType, Key } from "../../../../../models";
import { EditVehicleState } from "./EditVehicleState";
import { Actions, createNamespace } from "../../../../../flux/Actions";
import { FlagActions } from "../../../../flux/Flag/FlagActions";
import { ErrorActions } from "../../../../flux/Error/ErrorActions";
import { EditVehicleStore } from "./EditVehicleStore";
import { assertArray, assertValue } from "../../../../utils/assert/index";
import { Node, selfAndAncestors, Forest } from "../../../../../utils/Forest";
import { EditCreateEntityActions } from "../../../../actions/EditCreateEntityActions";
import { isValidBoxId } from "../../../../utils/draft/Vehicle";
import { CommitFn } from "../../../../../rest/Commit";
import { Polyglot } from "../../../../../polyglot";
import { appNavigator } from "../../../../routes";
import { UpsertCommand } from "../../commands/UpsertCommand";

const ns = createNamespace('EDIT_VEHICLE');
export const names = {
    COMPOUND_UPDATE: ns('SET_COMPOUND_UPDATE'),    
    SET_SOURCE: ns('SET_SOURCE'),
    SET_BOX_ID: ns('SET_BOX_ID'),
    SET_DISPLAY: ns('SET_DISPLAY'),
    SET_TAGS: ns('SET_TAGS'),
    SET_TIMEZONE: ns('SET_TIMEZONE'),
    SET_ALL_TAGS: ns('SET_ALL_TAGS'),
};

export class EditVehicleActions extends Actions
{
    update = (update: Update) => this.dispatch<Update>(names.COMPOUND_UPDATE, update);
    setSource = (source: Source) => this.dispatch<Source>(names.SET_SOURCE, source);
    setBoxId = (boxId: string) => this.dispatch<string>(names.SET_BOX_ID, boxId);
    setDisplay = (value: string) => this.dispatch<string>(names.SET_DISPLAY, value);
    setTags = (tags: string[]) => this.dispatch<string[]>(names.SET_TAGS, tags);
    setAllTags = (tags: string[]) => this.dispatch<string[]>(names.SET_ALL_TAGS, tags);
    setTimezone = (tzId: Key) => this.dispatch<Key>(names.SET_TIMEZONE, tzId);
}

export interface Update
{
    DivisionId?: number;
    allDivisions?: Division[];
    divisionsForest?: Forest<Division>;
    
    ServiceAccountId?: number;
    allServiceAccounts?: ServiceAccount[];
    filteredServiceAccounts?: ServiceAccount[];

    GeotabId?: string;
    GeotabTitle?: string;
    vehicles?: IdName[];
}

const createForest = (divisions: Division[]) => new Forest<Division>(d => d.id, d => d.ParentId, divisions);

export class UpsertVehicleController
{
    private vehicleRequestTicket = 0;
    private readonly actions = new EditVehicleActions();
    private readonly editEntityActions = new EditCreateEntityActions();
    private readonly errorActions = new ErrorActions();
    private readonly flagActions = new FlagActions();

    defaultTimezoneId: Key;
    
    constructor(
        private polyglot: Polyglot,
        private search: SearchFn,
        private getGeotabVehicles: GetGeotabVehiclesFn,
        private getVehicleTags: GetVehicleTagsFn,
        private commitEntity: CommitFn,
        private getCurrentDivisionId: () => number,
        private store: EditVehicleStore,
        private upsertCmd: UpsertCommand,
        private errorDispatchToken: string,
        private loadDivisionsDispatchToken: string,
        private loadServiceAccountsDispatchToken: string,
        private loadVehiclesDispatchToken: string,
        private loadTagsDispatchToken: string)
    {
    }

    showUpsertForm = (toEdit?: Vehicle) => {
        const createMode = toEdit == null;
        if(createMode) {
            this.editEntityActions.rejectDraft(EntityType.Vehicle);
            appNavigator.goToAddNewEntityTab(EntityType.Vehicle)
        } else {
            this.editEntityActions.setEntityToEdit(toEdit, EntityType.Vehicle);
            appNavigator.goToEditEntityTab(EntityType.Vehicle);
        }
        this.errorActions.clearError(this.errorDispatchToken);
    }

    cancel = () => {
        appNavigator.goToVehicleTab();
        this.editEntityActions.rejectDraft(EntityType.Vehicle);
        this.errorActions.clearError(this.errorDispatchToken);
    }

    upsert = () => {
        const { draft } = this.store.getState();
        const setError = (msg: string) => this.errorActions.setError(new Error(msg), this.errorDispatchToken);
        
        if(typeof draft.DivisionId !== 'number') {
            setError(this.polyglot.t('vehicle.validation_err.division_not_set'));
            return;
        }

        if(draft.SourceId === Source.BoxId) {
            if(!isValidBoxId(draft.BoxId)) {
                setError(this.polyglot.t('vehicle.validation_err.invalid_box_id'));
                return;
            }
        } else {
            if(typeof draft.ServiceAccountId !== 'number') {
                setError(this.polyglot.t('vehicle.validation_err.service_account_not_set'));
                return;
            } else if(typeof draft.GeotabId !== 'string') {
                setError(this.polyglot.t('vehicle.validation_err.geotab_vehicle_not_set'));
                return;
            }
        }

        this.upsertCmd.execute(draft, EntityType.Vehicle);
    }

    // This method is call by UI
    setDivision = (division: Division) => {
        // division selection was cleared
        const update: Update = { DivisionId: division ? division.id : null };
        if(!division) {
            this.actions.update(update);
            return;
        }
        
        const state = this.store.getState();
        
        // nothing changed
        if(division.id === state.draft.DivisionId) {
            return;
        }

        // When user choose some division. This value has to come from allDivisions.
        assertArray(state.allDivisions, 'Program error. allDivisions has to be loaded at this point.');
        assertValue(state.divisionsForest, 'divisionsForest is not set. Make sure divisionsForest depends on allDivisions');
        const isValidDivision = state.allDivisions.some(d => d.id === division.id);
        if(!isValidDivision) {
            throw new Error('Program error. Division value came not from source');
        }

        // New division value doesn't affect ServiceAccount and Vehicle fields in this case
        if(state.draft.SourceId === Source.Geotab) {
            const saState = this.computeServiceAccount(division.id, state.divisionsForest, state.draft.ServiceAccountId, state.allServiceAccounts);
            Object.assign(update, saState);
            
            const vehicleState = this.computeVehicleForServiceAccount(update.ServiceAccountId, state.draft.ServiceAccountId);
            Object.assign(update, vehicleState);

            const invalidVehicles = (update.ServiceAccountId != null) && (update.ServiceAccountId !== state.draft.ServiceAccountId);
            if(invalidVehicles) {
                this.fetchVehiclesFor(update.ServiceAccountId);
            }
        }

        this.actions.update(update);
    }

    setServiceAccount = (serviceAccountId: number) => {
        const update: Update = { ServiceAccountId: serviceAccountId };        
        const state = this.store.getState();

        const vehicleState = this.computeVehicleForServiceAccount(update.ServiceAccountId, state.draft.ServiceAccountId);
        Object.assign(update, vehicleState);

        const invalidVehicles = (update.ServiceAccountId != null) && (update.ServiceAccountId !== state.draft.ServiceAccountId);
        if(invalidVehicles) {
            this.fetchVehiclesFor(update.ServiceAccountId);
        }

        this.actions.update(update);
    }

    setVehicle = (vehicle: IdName) => {
        this.actions.update({ GeotabId: vehicle ? vehicle.Id : null, GeotabTitle: vehicle ? vehicle.Name : null });
    }

    // Computes Service account state for selected division
    private computeServiceAccount (divisionId: number, forest: Forest<Division>, oldServiceAccountId: number, allServiceAccounts: ServiceAccount[]) : Update
    {
        // allServiceAccounts or forest are not available => we can't compute filteredServiceAccounts and determine is current service account id valid or not
        if(divisionId == null || forest == null || allServiceAccounts == null)
            return {};
        
        const node = forest.getByKey(divisionId);
        const ids = new Set<number>(selfAndAncestors(node).map(d => d.id));
        const filteredServiceAccounts = allServiceAccounts.filter(sa => ids.has(sa.DivisionId));
        let serviceAccountId = filteredServiceAccounts.some(sa => sa.id === oldServiceAccountId) ? oldServiceAccountId : null;
        if(serviceAccountId === null && filteredServiceAccounts.length === 1) {
            serviceAccountId = filteredServiceAccounts[0].id;
        }

        let update: Update = { filteredServiceAccounts };
        if(serviceAccountId !== oldServiceAccountId) {
            update = {...update, ServiceAccountId: serviceAccountId };
        }
        return update;            
    }

    private computeVehicleForServiceAccount (newServiceAccountId: number, oldServiceAccountId: number): Update
    {
        if(newServiceAccountId == null)
            return {};

        if(newServiceAccountId === oldServiceAccountId)
            return {};

        // new value for service account ALWAYS invalidates vehicle and available vehicle options
        return { GeotabId: null, GeotabTitle: null, vehicles: null };
    }

    private computeVehicleForLoadedItems(oldVehicleId: string, vehicles: IdName[])
    {
        const update: Update = { vehicles };
        if(oldVehicleId === null && vehicles.length === 1) {
            update.GeotabId = vehicles[0].Id;
            update.GeotabTitle = vehicles[0].Name;
        }

        return update;
    }

    preload = () => {
        this.fetchDivisions();
        this.fetchServiceAccounts();
        
        const { SourceId, ServiceAccountId, id } = this.store.getState().draft;
        if(id == null) {
            this.actions.setTimezone(this.defaultTimezoneId);            
        }
        if(SourceId === Source.Geotab && ServiceAccountId != null) {
            this.fetchVehiclesFor(ServiceAccountId);
        }

        this.fetchTags();
    }

    private fetchDivisions()
    {
        this.flagActions.set(this.loadDivisionsDispatchToken);
        this.search({ Name: 'Divisions' })
            .then(res => {
                const allDivisions = res.Entries.Division;
                const divisionsForest = createForest(allDivisions);

                const state = this.store.getState();
                const update: Update = { allDivisions, divisionsForest, DivisionId: state.draft.DivisionId };

                if(update.DivisionId == null) {
                    const currDivisionId = this.getCurrentDivisionId();
                    if(divisionsForest.getByKey(currDivisionId)) {
                        update.DivisionId = currDivisionId;    
                    }
                }
                if(update.DivisionId == null && allDivisions.length === 1) {
                    update.DivisionId = allDivisions[0].id;
                }

                if(state.draft.SourceId === Source.Geotab) {
                    const saState = this.computeServiceAccount(update.DivisionId, update.divisionsForest, state.draft.ServiceAccountId, state.allServiceAccounts);
                    Object.assign(update, saState);
                    
                    const vehicleState = this.computeVehicleForServiceAccount(update.ServiceAccountId, state.draft.ServiceAccountId);
                    Object.assign(update, vehicleState);

                    const invalidVehicles = (update.ServiceAccountId != null) && (update.ServiceAccountId !== state.draft.ServiceAccountId);
                    if(invalidVehicles) {
                        this.fetchVehiclesFor(update.ServiceAccountId);
                    }
                }

                this.actions.update(update);                
            })
            .catch(err => {})
            .then(() => {
                this.flagActions.reset(this.loadDivisionsDispatchToken);
            }); // finaly
    }

    private fetchServiceAccounts()
    {
        this.flagActions.set(this.loadServiceAccountsDispatchToken);
        this.search(requestServiceAccounts())
            .then((resp: EntitySearchResult) => {
                const state = this.store.getState();
                
                const allServiceAccounts = resp.Entries.ServiceAccount;
                const saState = this.computeServiceAccount(state.draft.DivisionId, state.divisionsForest, state.draft.ServiceAccountId, allServiceAccounts);
                const update: Update = { allServiceAccounts, ...saState };
                
                this.actions.update(update);
                const invalidVehicles = (update.ServiceAccountId != null) && (update.ServiceAccountId !== state.draft.ServiceAccountId);
                if(invalidVehicles) {
                    this.fetchVehiclesFor(update.ServiceAccountId);
                }
            })
            .catch(err => {
                this.errorActions.setError(err, this.errorDispatchToken);
            })
            .then(() => {
                this.flagActions.reset(this.loadServiceAccountsDispatchToken);
            }); // finaly
    }

    private fetchVehiclesFor(serviceAccountId: number)
    {
        const ticket = ++this.vehicleRequestTicket;
        
        this.flagActions.set(this.loadVehiclesDispatchToken);
        this.getGeotabVehicles(serviceAccountId)
            .then(res => {
                if(ticket !== this.vehicleRequestTicket)
                    return;
                
                const { GeotabId } = this.store.getState().draft;
                const update = this.computeVehicleForLoadedItems(GeotabId, res.GeotabVehicles);
                this.actions.update(update);
            })
            .catch(err => {
                if(ticket !== this.vehicleRequestTicket)
                    return;

                this.errorActions.setError(err, this.errorDispatchToken);
            })
            .then(() => {
                if(ticket !== this.vehicleRequestTicket)
                    return;

                this.flagActions.reset(this.loadVehiclesDispatchToken);
            });
    }

    private fetchTags()
    {
        this.flagActions.set(this.loadTagsDispatchToken);
        this.getVehicleTags()
            .then(res => {
                this.actions.setAllTags(res.Tags);
            })
            .catch(err => {
                this.errorActions.setError(err, this.errorDispatchToken);
            })
            .then(() => {
                this.flagActions.reset(this.loadTagsDispatchToken);
            });
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Vehicle/flux/EditVehicleActions.ts